import { PlusOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { Button, Col, message, Popconfirm, Row, Space, Spin, Table, Tag, Tooltip } from 'antd';
import { TableProps } from 'antd/lib/table';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import useModal from 'hooks/useModal';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { scopesCannotChange } from 'services/api/department/permissions';
import {
  createRole,
  deleteRole,
  getRoleDetail,
  updateRole,
} from 'services/api/department/roleWithDepartment';
import { errorToasterWithServerMsg } from 'utils/errorToaster';
import { translateRole } from '../utils';
import AssignReceptionConfColumn from './components/AssignReceptionConfColumn';
import { useDepartmentDetailOfId } from './hooks/data';
import RoleDetailFormDrawer from './RoleDetail/RoleDetailFormDrawer';
import { RoleDescription } from './components/RoleDescription';

interface RoleWithDepartment extends StoreHomeV2.RoleWithDepartment {}

interface Department extends StoreHomeV2.Department {}

interface DepartmentDetail extends StoreHomeV2.DepartmentDetail {}

export interface RoleTableProps {
  departmentId?: Department['id'];
  agentCreate?: {
    name?: string;
    description?: string;
    presentation?: 'sidebar' | 'business';
    onClose(): void;
  };
}

const ActionButton = styled(Button)`
  padding: 0;
`;
type RoleModalMeta = {
  mode: 'create' | 'edit' | 'read';
  initialValues?: Partial<RoleWithDepartment>;
};
const RoleTable = (props: RoleTableProps) => {
  const { t } = useTranslate();

  const getPermission = useGetPermission();

  const editable = getPermission('帐号管理');

  const { departmentId } = props;
  const roleModal = useModal<RoleModalMeta>(
    Boolean(props.agentCreate),
    props.agentCreate
      ? {
          mode: 'create',
          initialValues: {
            name: props.agentCreate.name,
            description: props.agentCreate.description,
          },
        }
      : undefined,
  );
  const {
    data: departmentDetail,
    loading,
    refresh: refreshDept,
  } = useDepartmentDetailOfId(departmentId);
  const columns = useMemo(() => {
    const cols: TableProps<DepartmentDetail['roles'][number]>['columns'] = [
      {
        title: t('department.角色名称'),
        dataIndex: 'name',
        render: (name, record) => {
          return (
            <>
              {translateRole(name, t)}
              {record.isDefault === 1 ? <Tag color="red">{t('department.官')}</Tag> : null}
            </>
          );
        },
      },
      {
        title: t('department.角色描述'),
        dataIndex: 'description',
        render: (desc, record) => <RoleDescription value={desc} source={record} />,
      },
      {
        title: t('department.应用帐号数'),
        dataIndex: 'appliedCount',
        render: (appliedCount) => {
          return appliedCount || 0;
        },
      },
      {
        title: t('department.分配模式'),
        dataIndex: 'assignConversation',
        render: (_, record) => {
          const { isAdmin, adminStoreScopes, assignConversation, assignConversationConfig } =
            record;

          return (
            <AssignReceptionConfColumn
              isAdmin={isAdmin}
              adminStoreScopes={adminStoreScopes}
              assignConversation={assignConversation}
              assignConversationConfig={assignConversationConfig}
            />
          );
        },
      },
      {
        title: t('操作'),
        dataIndex: 'ctrl',
        render: (_, record) => {
          const getRole = () => getRoleDetail(record.id).then((r) => r.data.data);
          const showModal = async (mode: Exclude<RoleModalMeta['mode'], undefined>) => {
            try {
              const role = await getRole();
              roleModal.show({
                mode,
                initialValues: {
                  ...role,
                  name: translateRole(role.name, t),
                },
              });
            } catch (e) {
              errorToasterWithServerMsg(e, t('department.拉取角色数据失败，请稍后再试'));
            }
          };
          const { isDefault } = record;
          const allowDelete = record.appliedCount < 1;
          const shouldHide = isDefault || !editable;
          const deleteBtn = allowDelete ? (
            <>
              <Popconfirm
                title={t('department.你确定要删除该角色吗？')}
                onConfirm={() => {
                  deleteRole(record.id)
                    .then((r) => {
                      message.success(
                        t('department.已删除角色x', {
                          x: `<${record.name}>`,
                        }),
                      );
                      refreshDept();
                    })
                    .catch(errorToasterWithServerMsg);
                  // ocnso
                }}
              >
                <ActionButton type="link">{t('translation.删除')}</ActionButton>
              </Popconfirm>
            </>
          ) : (
            <Tooltip title={t('department.该角色下还有帐号，无法删除')}>
              <ActionButton type="link" style={{ color: 'rgba(0,0,0,.25)', cursor: 'not-allowed' }}>
                {t('translation.删除')}
              </ActionButton>
            </Tooltip>
          );
          return (
            <Space size={20}>
              <ActionButton
                type="link"
                onClick={() => {
                  showModal('read');
                }}
              >
                {t('translation.查看')}
              </ActionButton>
              {shouldHide ? null : (
                <>
                  <ActionButton
                    type="link"
                    onClick={() => {
                      showModal('edit');
                    }}
                  >
                    {t('translation.编辑')}
                  </ActionButton>
                  {deleteBtn}
                </>
              )}
            </Space>
          );
        },
      },
    ];
    return cols;
  }, [roleModal, editable, refreshDept, t]);
  const isReadMode = roleModal?.meta?.mode === 'read';
  const roles = departmentDetail?.roles ?? [];
  return (
    <Spin spinning={loading}>
      {!props.agentCreate && (
        <>
          <Row
            className={css`
              margin-bottom: 10px;
            `}
            justify="space-between"
            align="middle"
          >
            <Col>{t('feedback.共x项', { x: roles.length })}</Col>
            <Col>
              {editable && (
                <Button
                  onClick={() => {
                    roleModal.show({ mode: 'create' });
                  }}
                >
                  <PlusOutlined /> {t('department.新建角色')}
                </Button>
              )}
            </Col>
          </Row>
          <Table
            rowKey="id"
            dataSource={roles}
            columns={columns}
            pagination={{ showSizeChanger: true }}
          />
        </>
      )}
      {isNil(departmentDetail) ? null : (
        <RoleDetailFormDrawer
          presentation={props.agentCreate?.presentation}
          department={departmentDetail}
          title={t('department.角色')}
          destroyOnClose
          formProps={{
            disabled: isReadMode,
            initialValues: roleModal.meta?.initialValues,
            onFinish: async (values) => {
              const {
                rolePermScopeType,
                allScopes = [],
                permissionWithScopes,
                assignConversationConfig,
                ...others
              } = values;

              const submitValue = {
                ...others,
                rolePermScopeType,
                permissionWithScopes:
                  rolePermScopeType === 'DIFF_BY_PERM'
                    ? permissionWithScopes
                    : permissionWithScopes.map((permission) => {
                        const { permissionName } = permission;

                        if (scopesCannotChange.includes(permissionName)) {
                          return permission;
                        }

                        return {
                          ...permission,
                          platformStoreScopes: allScopes,
                        };
                      }),
                assignConversationConfig: {
                  ...assignConversationConfig,
                  platformStoreScopes:
                    rolePermScopeType === 'DIFF_BY_PERM'
                      ? assignConversationConfig.platformStoreScopes
                      : allScopes,
                },
              };

              const isCreate = isNil(submitValue.id);
              try {
                if (isCreate) {
                  await createRole({ ...submitValue, departmentId: departmentId! });
                  message.success(t('department.已创建角色'));
                } else {
                  await updateRole(submitValue);
                  message.success(t('department.已更新角色'));
                }
                refreshDept();
                roleModal.hide();
                props.agentCreate?.onClose();
              } catch (e) {
                errorToasterWithServerMsg(e);
              }
            },
          }}
          visible={roleModal.visible}
          onClose={() => {
            roleModal.hide();
            props.agentCreate?.onClose();
          }}
          onCopyAndCreate={() => {
            roleModal.update(true, {
              mode: 'create',
              initialValues: {
                ...roleModal.meta?.initialValues,
                isDefault: 0,
                name: undefined,
                id: undefined,
              },
            });
          }}
        />
      )}
    </Spin>
  );
};
export default RoleTable;
